import RadioGroupButton from '@acorn-portal/shared/src/components/inputs/radio-button-group';
import RadioCard from '@acorn-portal/shared/src/components/radioCard';
import { FinancePlan, FinancePlanWithDeposit } from '@acorn-portal/shared/src/services/renewalsApi';
import { formatCurrency } from '@acorn-portal/shared/src/utils';
import getDay from '@acorn-portal/shared/src/utils/get-day';
import { Box, Grid, Typography } from '@mui/material';
import { FC } from 'react';
import { useAppSelector } from '../../../hooks/reduxHook';

interface IMonthlyOption {
	selected: boolean;
	rates: FinancePlanWithDeposit | FinancePlan;
	annualPremium: number;
	name: string;
	title: string;
	insuranceTaxPercentage: number;
	policyAdministrationFee: number;
	onDepositChange: (value: string) => void;
	depositValue?: string;
	withDeposit?: boolean;
	paymentDay: number;
}

const MonthlyOption: FC<IMonthlyOption> = ({
	selected,
	rates,
	annualPremium,
	name,
	title,
	insuranceTaxPercentage,
	policyAdministrationFee,
	onDepositChange,
	depositValue,
	withDeposit,
	paymentDay,
}) => {
	const isUnequalInstallments = rates.unequalInstallmentAmount !== 0;
	const isDeposit = depositValue === 'yes';
	const fullPolicy = useAppSelector((state) => state.fullPolicy);
	const financedPolicy = import.meta.env.VITE_AUTH_REALM !== 'Motorcade';

	const getMonthsTotal = () => {
		if (rates.unequalInstallmentAmount) return rates.installmentCount! - 1;
		return rates.installmentCount!;
	};

	const getHeadline = () => {
		if (isDeposit) {
			return 'Initial Payment Due Today';
		}

		return isUnequalInstallments ? 'First Payment' : `${getMonthsTotal()} Monthly Payments of`;
	};

	const getHeadlineValue = () => {
		if (isDeposit) {
			return formatCurrency((rates as FinancePlanWithDeposit).depositAmount);
		}

		return isUnequalInstallments
			? formatCurrency(rates.unequalInstallmentAmount)
			: formatCurrency(rates.standardInstallmentAmount);
	};

	return (
		<RadioCard title={title} value={name} selected={selected}>
			<Grid container>
				<Grid sx={{ width: '100%' }}>
					<Typography sx={{ fontSize: 14, mt: 2 }}>{getHeadline()}</Typography>
					<Typography sx={{ fontWeight: 'bold', fontSize: 35, color: 'secondary.main' }}>
						{getHeadlineValue()}
					</Typography>
					<Grid
						container
						sx={{
							mt: 2,
							flex: 1,
							display: 'flex',
							gap: { xs: 3, md: 2 },
							rowGap: 5,
							flexWrap: 'wrap',
						}}
					>
						<Grid sx={{ minWidth: '22%' }}>
							<Typography sx={{ fontSize: 12, height: { xs: 'auto', sm: 'auto' } }}>Total</Typography>
							<Typography sx={{ fontSize: 14 }}>{formatCurrency(rates.totalPremium)}</Typography>
						</Grid>
						<Grid sx={{ minWidth: '22%' }}>
							<Typography sx={{ fontSize: 12, height: { xs: 'auto', sm: 'auto' } }}>Annual Premium</Typography>
							<Typography sx={{ fontSize: 14 }}>{formatCurrency(annualPremium)}</Typography>
						</Grid>
						{financedPolicy && (
							<>
								<Grid sx={{ minWidth: '22%' }}>
									<Typography sx={{ fontSize: 12, height: { xs: 'auto', sm: 'auto' } }}>Charge for credit</Typography>
									<Typography sx={{ fontSize: 14 }}>{formatCurrency(rates.chargeForCredit)}</Typography>
								</Grid>
								<Grid sx={{ minWidth: '22%' }}>
									<Typography sx={{ fontSize: 12, height: { xs: 'auto', sm: 'auto' } }}>APR</Typography>
									<Typography sx={{ fontSize: 14 }}>{rates.apr}%</Typography>
								</Grid>
							</>
						)}
						{isUnequalInstallments && isDeposit && (
							<Grid sx={{ minWidth: '22%' }}>
								<Typography sx={{ fontSize: 12, height: { xs: 'auto', sm: 'auto' } }}>First Payment</Typography>
								<Typography>{formatCurrency(rates.unequalInstallmentAmount)}</Typography>
							</Grid>
						)}
						{isUnequalInstallments && (
							<Grid sx={{ minWidth: '22%' }}>
								<Typography sx={{ fontSize: 12, height: { xs: 'auto', sm: 'auto' } }}>
									{getMonthsTotal()} Monthly Payments
								</Typography>
								<Typography>{formatCurrency(rates.standardInstallmentAmount)}</Typography>
							</Grid>
						)}
					</Grid>

					{withDeposit && fullPolicy.scheme !== 'Acorn6MTX' && (
						<Box
							sx={{
								py: 4,
								display: 'flex',
								textAlign: 'center',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								borderTop: '1px solid #e7e7e7',
								mt: 2,
								div: {
									display: 'inline-block',
									textAlign: 'center',
								},
								fieldset: {
									marginBottom: 0,
									px: 5,
								},
							}}
						>
							<RadioGroupButton
								label='Do you wish to make an Initial Payment?'
								name='deposit'
								id='depost'
								value={depositValue}
								topLabel
								onChange={onDepositChange}
							/>
							<Typography sx={{ fontSize: 14 }}>
								Please be aware that by selecting to make an initial payment this will lower the amount you need to pay
								each month
							</Typography>
						</Box>
					)}

					<Box
						sx={{
							py: 2,
							textAlign: 'left',
							borderTop: '1px solid #e7e7e7',
							mt: 0,
						}}
					>
						<Typography fontSize={12}>Payments will be taken on the {getDay(paymentDay)} of the month</Typography>
					</Box>
					<Typography
						sx={{
							fontSize: 14,
							borderTop: 1,
							borderColor: 'grey.300',
							pt: 2,
							mt: 0,
						}}
					>
						Prices include insurance premium tax of {insuranceTaxPercentage}% and a non-refundable policy administration
						fee of {formatCurrency(policyAdministrationFee)}
					</Typography>
				</Grid>
			</Grid>
		</RadioCard>
	);
};

export default MonthlyOption;
